import React, { useEffect, useState, useMemo } from 'react';
import * as api from '../../services/api';
import {
  Shield, Loader, Eye, Lock, Database, Building2, Zap,
  AlertTriangle, CheckCircle, ExternalLink,
} from 'lucide-react';

/**
 * PolicyCoverageTab — design-time preview of which L1 policy rules will
 * fire at runtime when this canvas node executes.
 *
 * <p>Renders next to Inputs / Output / Attributes on the right-side node
 * detail panel for any policy-related node (policy_enforcement_gate,
 * policy_rule_evaluation, dpg_scan, agent_autonomy_gate). Calls
 * GET /api/v1/policy/coverage with the node's enforcement point.</p>
 *
 * <p>The endpoint reads from the SAME PolicyRule rows the runtime would
 * consult, so what you see here is what will fire — minus the actual
 * evaluation result (which only shows up on the run's Enforcement
 * Audit timeline).</p>
 */

const COLORS = {
  S: { 0: 'var(--v2-bg-surface)', 50: 'var(--v2-bg-elevated)', 100: 'var(--v2-bg-muted)', 150: 'var(--v2-bg-muted)', 200: 'var(--v2-border-default)',
    300: 'var(--v2-border-default)', 400: 'var(--v2-text-disabled)', 500: 'var(--v2-text-muted)', 600: 'var(--v2-text-secondary)',
    700: 'var(--v2-text-secondary)', 800: 'var(--v2-text-primary)', 900: 'var(--v2-text-primary)' },
  brand: { primary: 'var(--v2-action)', light: 'var(--v2-action-soft)', soft: 'var(--v2-danger-soft)' },
  emerald: { bg: 'var(--v2-success-soft)', text: 'var(--v2-success)', border: 'var(--v2-success-soft)', icon: 'var(--v2-success)' },
  amber:   { bg: 'var(--v2-warning-soft)', text: 'var(--v2-warning)', border: 'var(--v2-warning-soft)', icon: 'var(--v2-warning)' },
  sky:     { bg: 'var(--v2-accent-cyan-soft)', text: 'var(--v2-accent-cyan)', border: 'var(--v2-accent-cyan-soft)', icon: 'var(--v2-accent-cyan)' },
  rose:    { bg: 'var(--v2-danger-soft)', text: 'var(--v2-accent-rose)', border: 'var(--v2-accent-rose-soft)', icon: 'var(--v2-accent-rose)' },
  violet:  { bg: 'var(--v2-accent-trust-soft)', text: 'var(--v2-accent-trust)', border: 'var(--v2-accent-trust-soft)', icon: 'var(--v2-accent-trust)' },
  slate:   { bg: 'var(--v2-bg-elevated)', text: 'var(--v2-text-secondary)', border: 'var(--v2-border-default)', icon: 'var(--v2-text-muted)' },
};

const CATEGORY_META = {
  PII:        { Icon: Eye,       accent: COLORS.violet,  label: 'PII' },
  CREDENTIAL: { Icon: Lock,      accent: COLORS.rose,    label: 'Credential' },
  FINANCIAL:  { Icon: Database,  accent: COLORS.amber,   label: 'Financial' },
  HEALTH:     { Icon: Shield,    accent: COLORS.sky,     label: 'Health' },
  CORPORATE:  { Icon: Building2, accent: COLORS.emerald, label: 'Corporate' },
  CUSTOM:     { Icon: Zap,       accent: COLORS.slate,   label: 'Custom' },
  SECURITY:        { Icon: Shield,    accent: COLORS.rose,   label: 'Security' },
  ACCESS_CONTROL:  { Icon: Lock,      accent: COLORS.sky,    label: 'Access Control' },
  DATA_CLASSIFICATION: { Icon: Database, accent: COLORS.violet, label: 'Data Classification' },
  COMPLIANCE:      { Icon: Shield,    accent: COLORS.sky,    label: 'Compliance' },
  OPERATIONAL:     { Icon: Zap,       accent: COLORS.emerald, label: 'Operational' },
};

const ACTION_COLOR = {
  BLOCK: COLORS.rose,
  WARN:  COLORS.amber,
  AUDIT: COLORS.sky,
  ALLOW: COLORS.emerald,
};

export default function PolicyCoverageTab({ node }) {
  // Resolve enforcement point from node config (set by GEL materializer or
  // user-typed at design time). Falls back to WORKFLOW_ENTRY which is the
  // most common Pre-Gate / Policy-Eval default and matches what the runtime
  // assumes when no point is specified.
  const enforcementPoint = useMemo(() => {
    const cfg = node?.config || node?.data?.config || {};
    return cfg.enforcementPoint
        || cfg.gelPhase                    // GEL phase doubles as enforcement point in many cases
        || (cfg.gelPhase === 'PRE_GATE' ? 'WORKFLOW_ENTRY' : null)
        || 'WORKFLOW_ENTRY';
  }, [node]);

  const agentId = useMemo(() => {
    const cfg = node?.config || node?.data?.config || {};
    return cfg.agentId || cfg.agent_id || null;
  }, [node]);

  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enforcementPoint) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.getPolicyCoverage({ enforcementPoint, agentId })
      .then((res) => {
        if (cancelled) return;
        if (res.ok) setCoverage(res.data);
        else setError(res.data?.message || 'Failed to load coverage');
      })
      .catch((e) => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [enforcementPoint, agentId]);

  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: COLORS.S[500] }}>
        <Loader size={20} style={{ animation: 'pctSpin 0.8s linear infinite' }} />
        <div style={{ fontSize: 12, marginTop: 8 }}>Loading policy coverage…</div>
        <style>{`@keyframes pctSpin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '12px 14px', background: COLORS.rose.bg,
        border: `1px solid ${COLORS.rose.border}`, color: COLORS.rose.text,
        borderRadius: 8, fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <AlertTriangle size={14} color={COLORS.rose.icon} />
        {error}
      </div>
    );
  }

  if (!coverage) return null;
  const totalRules = coverage.totalRules || 0;
  const blocking = coverage.blockingRules || 0;
  const warning = coverage.warningRules || 0;

  return (
    <div style={{ padding: '4px 0' }}>
      {/* Header explainer */}
      <div style={{
        background: COLORS.brand.light,
        border: `1px solid ${COLORS.brand.soft}`,
        borderRadius: 10, padding: '10px 12px', marginBottom: 14,
        display: 'flex', alignItems: 'flex-start', gap: 10,
      }}>
        <Shield size={14} color={COLORS.brand.primary} style={{ marginTop: 2, flexShrink: 0 }} />
        <div style={{ fontSize: 11, color: COLORS.S[700], lineHeight: 1.5 }}>
          Rules below evaluate at runtime when this node fires. This is a
          design-time preview — actual results are recorded in the workflow
          run's <strong>Enforcement Audit</strong> tab. Manage workflow-level
          assignment in{' '}
          <a href="/policy-config?tab=control-config"
             style={{ color: COLORS.brand.primary, fontWeight: 600 }}>
            Policy Config → Control Config <ExternalLink size={10} style={{ display: 'inline', verticalAlign: '-1px' }} />
          </a>{' '}or edit rules in{' '}
          <a href="/policy-config?tab=configurations"
             style={{ color: COLORS.brand.primary, fontWeight: 600 }}>
            Policy Skills <ExternalLink size={10} style={{ display: 'inline', verticalAlign: '-1px' }} />
          </a>.
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <Stat label="Enforcement point" value={enforcementPoint} accent={COLORS.slate} compact />
        <Stat label="Rules" value={totalRules} accent={COLORS.sky} />
        {blocking > 0 && <Stat label="Block" value={blocking} accent={COLORS.rose} />}
        {warning > 0 && <Stat label="Warn" value={warning} accent={COLORS.amber} />}
      </div>

      {totalRules === 0 ? (
        <div style={{ padding: 18, textAlign: 'center', color: COLORS.S[500],
          background: COLORS.S[50], border: `1px dashed ${COLORS.S[200]}`, borderRadius: 8 }}>
          <CheckCircle size={20} color={COLORS.S[300]} />
          <div style={{ fontSize: 12, fontWeight: 600, marginTop: 8 }}>
            No rules configured for {enforcementPoint}
          </div>
          <div style={{ fontSize: 11, marginTop: 4 }}>
            Authoring rules in Policy Skills will surface them here automatically.
          </div>
        </div>
      ) : (
        (coverage.categories || []).map((g) => (
          <CategoryGroup key={g.category} group={g} />
        ))
      )}
    </div>
  );
}

function Stat({ label, value, accent, compact }) {
  return (
    <div style={{
      flex: compact ? 'auto' : '1 1 70px', minWidth: compact ? 'auto' : 70,
      padding: '6px 10px', borderRadius: 6,
      background: accent.bg, border: `1px solid ${accent.border}`,
    }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: accent.text,
        textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: accent.text, marginTop: 2,
        fontFamily: compact ? 'ui-monospace, monospace' : 'inherit' }}>
        {value}
      </div>
    </div>
  );
}

function CategoryGroup({ group }) {
  const meta = CATEGORY_META[group.category] || CATEGORY_META.CUSTOM;
  return (
    <div style={{ marginBottom: 10, background: 'var(--v2-bg-surface)',
      border: `1px solid ${COLORS.S[200]}`, borderRadius: 8, overflow: 'hidden' }}>
      <div style={{
        padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8,
        background: meta.accent.bg, borderBottom: `1px solid ${meta.accent.border}`,
      }}>
        <meta.Icon size={12} color={meta.accent.icon} />
        <span style={{ fontSize: 11, fontWeight: 700, color: meta.accent.text,
          textTransform: 'uppercase', letterSpacing: '0.06em' }}>{meta.label}</span>
        <span style={{ marginLeft: 'auto', fontSize: 10, color: COLORS.S[600] }}>
          {group.count} rule{group.count !== 1 ? 's' : ''}
        </span>
      </div>
      {(group.rules || []).map((r) => (
        <RuleRow key={r.id} rule={r} />
      ))}
    </div>
  );
}

function RuleRow({ rule }) {
  const action = (rule.violationAction || 'AUDIT').toUpperCase();
  const ac = ACTION_COLOR[action] || COLORS.slate;
  return (
    <div style={{ padding: '8px 12px', borderBottom: `1px solid ${COLORS.S[150]}`,
      display: 'flex', alignItems: 'center', gap: 8, opacity: rule.enabled === false ? 0.5 : 1 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.S[900],
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {rule.name}
        </div>
        {rule.description && (
          <div style={{ fontSize: 10, color: COLORS.S[500], marginTop: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {rule.description}
          </div>
        )}
      </div>
      <span style={{ fontSize: 9, color: COLORS.S[600], fontFamily: 'ui-monospace, monospace' }}>
        {rule.ruleType}
      </span>
      <span style={{ fontSize: 10, fontWeight: 700, color: ac.text,
        padding: '2px 6px', borderRadius: 4, background: ac.bg, border: `1px solid ${ac.border}`,
        textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {action}
      </span>
    </div>
  );
}
